// src/cockpit4/components/ProfitBySkuTable.tsx
// ============================================
// V1.5 P0-4：产品级毛利归因表（Profit Engine）
//
// 数据：RuntimeManager.execute(RevenueEngine, 'profit_by_sku')
// 真实聚合 record_sale 记录；成本未配置时显式标注，不伪造毛利率。
// 高毛利 → 加投建议；低毛利 → 止损/涨价建议，供 CEO 决策引擎消费。
// ============================================

import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PieChart, Loader2, RefreshCw, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2 } from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import type { ProfitBySku } from "../../runtime/RevenueEngine/types";

/** 金额格式化 */
function fmtMoney(v: number): string {
  if (v >= 10000) return `${(v / 10000).toFixed(1)} 万`;
  return v.toLocaleString("zh-CN");
}

const STATUS_META: Record<string, { label: string; color: string; icon: typeof CheckCircle2 }> = {
  profitable: { label: "盈利", color: "#34D399", icon: CheckCircle2 },
  breakeven: { label: "持平", color: "#FBBF24", icon: TrendingUp },
  loss: { label: "亏损", color: "#F87171", icon: TrendingDown },
  unknown: { label: "未定", color: "#6B7280", icon: AlertTriangle },
};

export default function ProfitBySkuTable() {
  const [data, setData] = useState<ProfitBySku | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!RuntimeManager.isInitialized() || !RuntimeManager.hasRuntime(RuntimeName.RevenueEngine)) {
        setError("收入引擎未就绪");
        setData(null);
        return;
      }
      const resp = await RuntimeManager.execute(RuntimeName.RevenueEngine, "profit_by_sku", {});
      if (resp?.success && resp.data) {
        setData(resp.data as ProfitBySku);
      } else {
        setError(resp?.error || "获取失败");
        setData(null);
      }
    } catch (e: any) {
      setError(e?.message || "获取失败");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // 汇总
  const totalRevenue = data?.products.reduce((s, p) => s + p.revenue, 0) || 0;
  const totalProfit = data?.products.reduce((s, p) => s + p.grossProfit, 0) || 0;
  const configuredCount = data?.products.filter((p) => p.costConfigured).length || 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <PieChart size={12} className="text-emerald-400" />
        <h3 className="text-[12px] font-medium text-white">产品毛利归因</h3>
        <span className="text-[9px] font-mono text-gray-500">Profit Engine · 哪条业务线真赚钱</span>
        {data && (
          <span className="text-[9px] font-mono text-gray-600">
            {data.products.length} 条 · 收入 ¥{fmtMoney(totalRevenue)} · 毛利 ¥{fmtMoney(totalProfit)}
          </span>
        )}
        <button
          onClick={() => void load()}
          disabled={loading}
          className="ml-auto flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 transition-colors disabled:opacity-50"
          title="重新聚合产品毛利"
        >
          {loading ? (
            <Loader2 size={10} className="text-emerald-400 animate-spin" />
          ) : (
            <RefreshCw size={10} className="text-emerald-400" />
          )}
          <span className="text-[9px] font-mono text-emerald-300 uppercase tracking-wider">刷新</span>
        </button>
      </div>

      {loading ? (
        <div className="py-8 text-center">
          <Loader2 size={16} className="mx-auto text-emerald-400 animate-spin mb-2" />
          <p className="text-[10px] font-mono text-gray-500">聚合产品收入与成本...</p>
        </div>
      ) : error || !data || data.products.length === 0 ? (
        <div className="py-6 text-center rounded-md border border-dashed border-white/[0.08]">
          <p className="text-[10px] font-mono text-gray-500">
            {error || "暂无成交记录 — 收入录入后自动聚合毛利"}{" "}
            <span className="text-gray-600">（成本未配置会显式标注，不伪造毛利率）</span>
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[9px] font-mono text-gray-500 uppercase tracking-wider border-b border-white/[0.04]">
                <th className="py-1.5 pr-2">产品</th>
                <th className="py-1.5 pr-2">收入</th>
                <th className="py-1.5 pr-2">笔数</th>
                <th className="py-1.5 pr-2">成本</th>
                <th className="py-1.5 pr-2">毛利</th>
                <th className="py-1.5 pr-2">毛利率</th>
                <th className="py-1.5">Hermes 建议</th>
              </tr>
            </thead>
            <tbody>
              {data.products.map((p, idx) => {
                const meta = STATUS_META[p.status] || STATUS_META.unknown;
                const Icon = meta.icon;
                return (
                  <tr
                    key={p.productId + idx}
                    className="text-[10px] font-mono border-b border-white/[0.03] last:border-b-0 hover:bg-white/[0.02]"
                  >
                    <td className="py-2 pr-2">
                      <div className="flex items-center gap-1.5">
                        <Icon size={10} style={{ color: meta.color }} />
                        <span className="text-gray-200">{p.productName}</span>
                      </div>
                    </td>
                    <td className="py-2 pr-2 text-white">¥{fmtMoney(p.revenue)}</td>
                    <td className="py-2 pr-2 text-gray-400">{p.count}</td>
                    <td className="py-2 pr-2 text-gray-400">
                      {p.costConfigured ? `¥${fmtMoney(p.cost)}/件` : "未配置"}
                    </td>
                    <td
                      className="py-2 pr-2"
                      style={{ color: p.costConfigured ? (p.grossProfit >= 0 ? "#34D399" : "#F87171") : "#6B7280" }}
                    >
                      {p.costConfigured ? `¥${fmtMoney(p.grossProfit)}` : "—"}
                    </td>
                    <td className="py-2 pr-2">
                      {p.marginRate !== null ? (
                        <span style={{ color: p.marginRate >= 0.7 ? "#34D399" : p.marginRate <= 0.3 ? "#F87171" : "#FBBF24" }}>
                          {(p.marginRate * 100).toFixed(0)}%
                        </span>
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                    <td className="py-2">
                      <span
                        className="px-1.5 py-0.5 rounded text-[9px]"
                        style={{ background: `${meta.color}15`, color: meta.color }}
                      >
                        {p.recommendation}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && data.products.length > 0 && configuredCount < data.products.length && (
        <p className="mt-2 text-[9px] font-mono text-gray-600">
          提示：{data.products.length - configuredCount} 条产品未配置交付成本，毛利率显示「—」，配置后 Hermes 才能给出加投/止损建议
        </p>
      )}
    </motion.div>
  );
}
